/**
 * This file contains the mapping of sprite keys for each of the dynamic spatial
 * layers currently supported in RSP.
 *
 * Why is this file needed?
 *
 * Andromeda allows for almost complete setup of dynamic spatial layers except
 * for their marker images (icons) - so RSP currently ships with images that
 * correspond to each of the spatial layers.
 *
 * How should I update this file?
 *
 * If adding or changing an icon, first save it in the Map/assets folder
 * (preferably as an SVG) and then update Map/assets/sprites-manifest.json to
 * include the name of the file. This will ensure that the sprite is included in
 * the sprite generation process.
 *
 * How do I generate an updated sprite?
 *
 * Go to dev_scripts/sprites-gen and run: `yarn start`. sprites-gen will
 * read the Map/assets/sprites-manifest.json and generate the updated sprite
 * and sprites-info.json file.
 *
 * The sprites-info.json file is imported by DynamicLayerGL to get the
 * coordinates of the sprite image.
 *
 * See dev_scripts/sprites-gen/README.md for more information.
 *
 * How does this file work?
 *
 * The sprite generation process generates a sprites-info.json file that
 * contains the coordinates of each of the sprites in the sprite image. This
 * file is imported here and used to map the sprite keys to the coordinates.
 *
 * Use it like this:
 *
 * import {
 *   spritesImage,
 *   spritesMapping,
 *   getDynamicSpritesIcon
 * } from './getDynamicSpritesIcon';
 *
 * new IconLayer({
 *  iconAtlas: spritesImage,
 *  iconMapping: spritesMapping,
 *  getIcon: (d) => {
 *   const iconState = d.id === activeMarkerId ? 'active' : 'default';
 *   return getDynamicSpritesIcon(spatialLayerKey, iconState, d);
 *  }
 * })
 *
 */
import spritesMapping from '../../assets/sprites-output/sprites-info.json';
import spritesImage from '../../assets/sprites-output/sprites_2x.png';

const SPATIAL_LAYER_KEYS = {
  ArcherDrone: 'archer_drone',
  ArcherDestination: 'archer_drop',
  AviveAppUser: 'avive_app',
  AviveDevice: 'avive_aed',
  AviveRequest: 'avive_aed_request',
  AvivePlaceholder: 'avive_aed_placeholder',
  AviveHighlight: 'avive_aed_highlight',
  IARResponder: 'iar_responders',
  IARVehicle: 'iar_vehicles',
  PublicSonarCrowdControl: 'public_sonar_crowd_control',
  PublicSonarInfrastructure: 'public_sonar_vital_infrastructure',
  PublicSonarMobility: 'public_sonar_mobility',
  PublicSonarNaturalDisaster: 'public_sonar_natural_disaster',
  PublicSonarPublicSafety: 'public_sonar_public_safety',
  RollKall: 'rollkall',
  AutoReturn: 'autoreturn',
};

// Map spatial layer keys to sprite keys.
const sprites = {
  default: {
    default: 'dsl_default',
    active: 'dsl_default_selected',
  },
  [SPATIAL_LAYER_KEYS.AviveAppUser]: {
    default: 'avive_appuser_accepted_default',
    active: 'avive_appuser_accepted_active',
  },
  [SPATIAL_LAYER_KEYS.AviveDevice]: {
    accepted: {
      default: 'avive_aed_accepted_default',
      active: 'avive_aed_accepted_active',
    },
    unaccepted: {
      default: 'avive_aed_unaccepted_default',
      active: 'avive_aed_unaccepted_active',
    },
  },
  [SPATIAL_LAYER_KEYS.AviveRequest]: {
    default: 'avive_aed_request_default',
    active: 'avive_aed_request_active',
    inactive: 'avive_aed_request_inactive',
  },
  // NOTE: this isn't an official layer, it's a placeholder for Avive's
  // address suggestion feature. See AddressLocator where it's created.
  [SPATIAL_LAYER_KEYS.AvivePlaceholder]: {
    default: 'avive_aed_request_default',
    active: 'avive_aed_request_active',
  },
  // NOTE: this isn't an official layer, it's a placeholder for Avive's
  // address suggestion feature. See AddressLocator where it's created.
  [SPATIAL_LAYER_KEYS.AviveHighlight]: {
    default: 'avive_aed_request_temp',
    active: 'avive_aed_request_temp',
  },
  [SPATIAL_LAYER_KEYS.ArcherDrone]: {
    default: 'archer_drone_default',
    active: 'archer_drone_selected',
  },
  [SPATIAL_LAYER_KEYS.ArcherDestination]: {
    default: 'archer_destination_default',
    active: 'archer_destination_selected',
  },
  [SPATIAL_LAYER_KEYS.IARResponder]: {
    default: 'iar_responder_other',
    active: 'iar_responder_other_selected',
  },
  [SPATIAL_LAYER_KEYS.IARVehicle]: {
    ems: {
      default: 'iar_vehicle_ems',
      active: 'iar_vehicle_ems_selected',
    },
    fire: {
      default: 'iar_vehicle_fire',
      active: 'iar_vehicle_fire_selected',
    },
    other: {
      default: 'iar_vehicle_other',
      active: 'iar_vehicle_other_selected',
    },
    police: {
      default: 'iar_vehicle_police',
      active: 'iar_vehicle_police_selected',
    },
  },
  [SPATIAL_LAYER_KEYS.PublicSonarCrowdControl]: {
    default: 'publicsonar_crowdcontrol_default',
    active: 'publicsonar_crowdcontrol_selected',
  },
  [SPATIAL_LAYER_KEYS.PublicSonarInfrastructure]: {
    default: 'publicsonar_infrastructure_default',
    active: 'publicsonar_infrastructure_selected',
  },
  [SPATIAL_LAYER_KEYS.PublicSonarMobility]: {
    default: 'publicsonar_mobility_default',
    active: 'publicsonar_mobility_selected',
  },
  [SPATIAL_LAYER_KEYS.PublicSonarNaturalDisaster]: {
    default: 'publicsonar_naturaldisaster_default',
    active: 'publicsonar_naturaldisaster_selected',
  },
  [SPATIAL_LAYER_KEYS.PublicSonarPublicSafety]: {
    default: 'publicsonar_publicsafety_default',
    active: 'publicsonar_publicsafety_selected',
  },
  [SPATIAL_LAYER_KEYS.RollKall]: {
    default: 'rollkall_default',
    active: 'rollkall_selected',
  },
  [SPATIAL_LAYER_KEYS.AutoReturn]: {
    default: 'tow_truck_default',
    active: 'tow_truck_selected',
  },
};

/**
 * Improve app resilience by matching spatial layer keys that resemble a layer.
 *
 * This sometimes happens when the Implementation Team breaks naming conventions
 * across stacks. For example, we expect `rollkall` but the layer is being
 * suffixed with the stack, e.g: `rollkall_sandbox`, `rollkall_staging`, etc.
 *
 * The Implementation Team is aware that a naming convention must be used going
 * forward, but layers like this exist in production and we need to support them.
 *
 * @param {*}
 * @param {*} targetLayerKey
 * @returns
 */
export const matchIdenticalLayerKey = targetLayerKey => {
  // First check if there's an exact match.
  const exact = Object.values(SPATIAL_LAYER_KEYS).find(key => {
    return targetLayerKey.toLowerCase() === key.toLowerCase();
  });

  if (exact) {
    return exact;
  }

  // Otherwise, if there's a partial match, use that as the result.
  const result = Object.values(SPATIAL_LAYER_KEYS).find(key => {
    return targetLayerKey.toLowerCase().includes(key.toLowerCase());
  });

  // If there's no match, return the targetLayerKey that was passed in.
  if (!result) {
    return targetLayerKey;
  }

  return result;
};

/**
 * Get the sprite icon key for a spatial layer key.
 * @param {string} spatialLayerKey
 * @param {string} iconState - default or active
 * @param {Object} spatialObject
 * @returns
 */
const getDynamicSpritesIcon = (
  spatialLayerKey,
  iconState = 'default',
  spatialObject = {},
) => {
  const matchedLayerKey = matchIdenticalLayerKey(spatialLayerKey);
  const spriteStates = sprites[matchedLayerKey];

  // Return sprites.default when no sprite is found for the spatial layer key.
  // This can happen in early states of a partner being onboarded where the
  // Implementation Team is provisioning their spatial layer and testing it out
  // before we've updated the code with their icons.
  if (!spriteStates) {
    return sprites.default[iconState];
  }

  // If it's an Avive Device, we need to check if the device is accepted or not.
  if (matchedLayerKey === SPATIAL_LAYER_KEYS.AviveDevice) {
    const resourceAccepted = spatialObject?.attributes?.resource_accepted || '';
    const isAccepted = `${resourceAccepted}`.toLowerCase() === 'true';

    if (isAccepted) {
      return spriteStates.accepted[iconState];
    }
    return spriteStates.unaccepted[iconState];
  }

  // If it's an IAR Vehicle, we need to check the vehicle type.
  if (matchedLayerKey === SPATIAL_LAYER_KEYS.IARVehicle) {
    const apparatusType =
      spatialObject?.attributes?.vehicle_details?.ApparatusTypeName || '';
    const vehicleType = `${apparatusType}`.toLowerCase();
    const vehicleStates = spriteStates[vehicleType] || spriteStates.other;
    return vehicleStates[iconState];
  }

  return spriteStates[iconState];
};

export { spritesImage, spritesMapping, getDynamicSpritesIcon };
export default getDynamicSpritesIcon;
